/* eslint-disable max-lines, yoda */
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { AvcActions, ErrorMessage } from './enum';
import { addressSaga, tivanoSaga, updateOffersAfterVpSaga } from './util/generator';
import { debugInfo } from '../../util/debug';
import {
    setLoading,
    setError,
    setAddresses,
    setAccessSupplier,
    setRolloutEndMonth,
    setRolloutStatus,
    setAvailabilityIndicator,
} from './action';
import { setLoading as setLpLoading, setPreMarketing } from '../App/action';
import { selectFF, selectGigakombi, selectTariff } from '../App/selector';
import { trackError, trackPageView } from '../../util/tracking';
import { TrackingAvcStep } from '../../enums/tracking';
import { setActiveProviderType, setAvcResult, setSelectedTariffId } from '../App/action';
import { deleteCookie, isProdEnv, setCookie } from '@vfde-sails/utils';
import { Cookies } from '../../enums/network';
import {
    Address,
    GimliData,
    JarvisData,
    JarvisProduct,
    TivanoData,
    getGimliData,
    toAvcResult,
    getJarvisSuggestionData,
    JarvisRequestData,
    AvailabilityIndicator,
} from '@vfde-sails/avc-core';
import { selectAddress } from '../App/selector';
import { getAvailableProvider, getNewSelectedMetacode, isProviderAvailable } from './util/providerSwitcher';
import { selectProviderType, selectSelected, selectTariffs } from 'Container/App/selector';
import { MetacodePrefix, ProviderType } from '../../enums/product';
import { Selected } from 'Container/App/interface';
import { getOffersSaga } from 'Container/App/saga';
import { isAvcError } from './util/typeGuard';
import { GELB_GRAU, GRUEN, GRUEN_GRAU, ORANGE, ORANGE_ROT, VF_COOKIE_DOMAIN, ZHP_NO_FIBER_TRIGGER } from './constants';
import { Action } from 'redux';
import { filterOnlyCableMetacodes } from './util/jarvisHelper';

function* seamlessProviderSaga(tivanoMetacodes: string[]) {
    const currentProvider: ProviderType = yield select(selectProviderType);
    const isAnyProductAvailable = isProviderAvailable(currentProvider, tivanoMetacodes);

    if (isAnyProductAvailable) {
        return;
    }

    const availableProvider = getAvailableProvider(currentProvider, tivanoMetacodes);
    const selected: Selected = yield select(selectSelected);
    const { tariffId } = selected;

    if (!availableProvider || !tariffId) {
        return;
    }

    const newSelectedMetacode = getNewSelectedMetacode(currentProvider, tariffId, availableProvider);

    debugInfo('Seamless provider switch: ', newSelectedMetacode);

    if (!newSelectedMetacode) {
        return;
    }

    // Seamless set new provider to state
    yield put(setActiveProviderType(availableProvider));
    // Seamless set new provider's tariff to state
    yield put(setSelectedTariffId(newSelectedMetacode));

    const tariffs: JarvisProduct[] = yield select(selectTariffs);

    // Re-call getOffers Jarvis if new products aren't in the state
    if (!tariffs.length) {
        yield getOffersSaga();
    }

    return newSelectedMetacode;
}
/**
 * set wholebuy data to state eg:
 *  accessSupplier
 *  rolloutStatus
 * rolloutEndMonth
 * availabilityIndicator
 */
export function* setFiberDataToState(tivano: TivanoData) {
    let accessSupplier = '';
    let rolloutEndMonth = '';
    let rolloutStatus = '';
    let availabilityIndicator: AvailabilityIndicator | '' = '';
    let fiberProductCodes = tivano.metacodes.filter(str => str.includes(MetacodePrefix.FIBER));
    for (const code of fiberProductCodes) {
        if (tivano.products?.[code]) {
            if (tivano.products[code].availabilityIndicator === GRUEN) {
                accessSupplier = tivano.products[code].accessSupplier || '';
                rolloutEndMonth = tivano.products[code].rolloutEndMonth || '';
                rolloutStatus = tivano.products[code].rolloutStatus || '';
                availabilityIndicator = tivano.products[code].availabilityIndicator || '';
                break;
            } else if (tivano.products[code].availabilityIndicator === GELB_GRAU) {
                accessSupplier = tivano.products[code].accessSupplier || '';
                rolloutEndMonth = tivano.products[code].rolloutEndMonth || '';
                rolloutStatus = tivano.products[code].rolloutStatus || '';
                availabilityIndicator = tivano.products[code].availabilityIndicator || '';
            } else if (tivano.products[code].availabilityIndicator === ORANGE) {
                accessSupplier = tivano.products[code].accessSupplier || '';
                rolloutEndMonth = tivano.products[code].rolloutEndMonth || '';
                rolloutStatus = tivano.products[code].rolloutStatus || '';
                availabilityIndicator = tivano.products[code].availabilityIndicator || '';
            } else if (tivano.products[code].availabilityIndicator === GRUEN_GRAU) {
                accessSupplier = tivano.products[code].accessSupplier || '';
                rolloutEndMonth = tivano.products[code].rolloutEndMonth || '';
                rolloutStatus = tivano.products[code].rolloutStatus || '';
                availabilityIndicator = tivano.products[code].availabilityIndicator || '';
            } else if (tivano.products[code].availabilityIndicator === ORANGE_ROT) {
                accessSupplier = tivano.products[code].accessSupplier || '';
                rolloutEndMonth = tivano.products[code].rolloutEndMonth || '';
                rolloutStatus = tivano.products[code].rolloutStatus || '';
                availabilityIndicator = tivano.products[code].availabilityIndicator || '';
            }
        }
    }
    yield put(setAccessSupplier(accessSupplier));
    yield put(setRolloutEndMonth(rolloutEndMonth));
    yield put(setRolloutStatus(rolloutStatus));
    yield put(setAvailabilityIndicator(availabilityIndicator));
    return { accessSupplier, rolloutEndMonth, rolloutStatus, availabilityIndicator };
}

export function* availabilityCheckSaga(
    _?: Action,
    isSeamless: boolean = false
) {
    try {
        const selectedTariff: JarvisProduct = yield select(selectTariff);
        const gigakombi: boolean = yield select(selectGigakombi);
        const ff: boolean = yield select(selectFF);

        if (!isSeamless) {
            trackPageView(selectedTariff, gigakombi, ff, undefined, TrackingAvcStep.START);
        }

        yield put(setLoading());
        yield put(setError(undefined));
        yield put(setPreMarketing(undefined));

        const address: Address = yield addressSaga();

        if (!address) {
            throw new Error(ErrorMessage.EMPTY_RESPONSE);
        }

        const gimli: GimliData = yield call(getGimliData, address);
        const tivano: TivanoData = yield call(tivanoSaga, address, isSeamless);
        let { accessSupplier,
            rolloutEndMonth,
            rolloutStatus,
            availabilityIndicator,
        } = yield setFiberDataToState(tivano);

        if (!tivano || !gimli) {
            throw new Error(ErrorMessage.EMPTY_RESPONSE);
        }

        const newSelectedMetacode: string | undefined = yield call(seamlessProviderSaga, tivano.metacodes);
        const request: JarvisRequestData = {
            salesChannel: 'FIB',
            selectedId: newSelectedMetacode || selectedTariff?.id,
            isGigakombi: gigakombi,
            isFriendsAndFamily: ff,
            productId: [
                ...filterOnlyCableMetacodes(Object.keys(gimli.products)),
                ...tivano.metacodes,
            ],
            gimli: gimli.products,
            tivano: tivano.products,
            ...(rolloutEndMonth && { rolloutEndMonth }),
            ...(rolloutStatus && { rolloutStatus }),
        };

        if (accessSupplier) {
            request.accessSupplier = accessSupplier;
            request.availabilityIndicator = availabilityIndicator;
        } else {
            yield put(setAccessSupplier(''));
        }

        const jarvis: JarvisData = yield call(getJarvisSuggestionData, request);
        const avcResult = toAvcResult(gimli, tivano, jarvis);
        if (avcResult && !avcResult.availability.fiber) {
            setCookie(Cookies.ZHP_LM_MODULE, {
                value: ZHP_NO_FIBER_TRIGGER,
                domain: isProdEnv() ? VF_COOKIE_DOMAIN : '',
            });
        } else {
            deleteCookie(Cookies.ZHP_LM_MODULE);
        }

        yield call(updateOffersAfterVpSaga, tivano.products);

        if (!isSeamless) {
            trackPageView(selectedTariff,
                gigakombi, ff,
                avcResult, TrackingAvcStep.COMPLETE, undefined,
                accessSupplier, availabilityIndicator, tivano);
        }

        yield put(setAvcResult(avcResult));

        setCookie(Cookies.ADDRESS, {
            value: btoa(JSON.stringify(address)),
            domain: isProdEnv() ? VF_COOKIE_DOMAIN : '',
        });
    } catch (error) {
        debugInfo('Error in avc flow: ', error);

        yield put(setError(error as Error));

        if (isAvcError(error)) {
            yield trackError(error, true);
        }
    } finally {
        yield put(setLoading(false));
    }
}

export function* getEntryStateSaga() {
    const address: Address = yield select(selectAddress);

    if (address) {
        yield put(setLpLoading());
        yield put(setAddresses([address]));
        yield call(availabilityCheckSaga, undefined, true);
        yield put(setLpLoading(false));
    }
}

/**
 * Availability check saga
 *
 * @export {function} saga
 */
export default function* saga() {
    yield takeLatest(AvcActions.GET_ENTRY_STATE, getEntryStateSaga);
    yield takeLatest(AvcActions.CHECK_ADDRESS, availabilityCheckSaga);
}
